// src/components/steps/EducationalBackground.jsx
import React, { useState, useEffect } from "react";
import EducationIcon from "../../assets/ProfileSetup/EducationalBackground.svg"; // Adjust path
import SkipToDashboardButton from "../../components/dashboard/common/SkipToDashboardButton";
import { TrashIcon, PencilIcon } from "@heroicons/react/24/outline";
import "../../css/EducationalBackground.css";

// --- UPDATED: Reusable Saving Animation Component ---
const SavingAnimation = ({ isSaving, message, subMessage }) => {
  if (!isSaving) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-white bg-opacity-80 backdrop-blur-sm flex flex-col items-center justify-center z-50 transition-opacity duration-300"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="text-center p-8 rounded-lg">
        <svg
          className="animate-spin h-10 w-10 text-emerald-600 mb-4 mx-auto"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p className="text-lg font-semibold text-gray-800">
          {message || "Saving..."}
        </p>
        <p className="text-sm text-gray-600 mt-1">
          {subMessage || "Please wait a moment."}
        </p>
      </div>
    </div>
  );
};

const initialEducationState = {
  id: null,
  institutionName: "",
  institution_id: null,
  degree: "",
  fieldOfStudy: "",
  startDate: "",
  endDate: "",
  description: "",
};

function EducationalBackground({
  educations = [],
  onAddEducation,
  onUpdateEducation,
  onRemoveEducation,
  onNext,
  onBack,
  isSubmitting,
  onSkipToDashboard,
}) {
  const [currentEducation, setCurrentEducation] = useState(
    initialEducationState
  );
  const [editingIndex, setEditingIndex] = useState(null);
  const [formError, setFormError] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const isLoading = isSubmitting || isNavigating;

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (editingIndex !== null && educations[editingIndex]) {
      const eduToEdit = educations[editingIndex];
      setCurrentEducation({
        id: eduToEdit.id,
        institutionName: eduToEdit.institution?.name || "",
        institution_id: eduToEdit.institution_id,
        degree: eduToEdit.degree || "",
        fieldOfStudy: eduToEdit.field_of_study || "",
        startDate: eduToEdit.start_date
          ? eduToEdit.start_date.substring(0, 10)
          : "",
        endDate: eduToEdit.end_date ? eduToEdit.end_date.substring(0, 10) : "",
        description: eduToEdit.description || "",
      });
      window.scrollTo(0, 0);
    } else if (editingIndex === null) {
      setCurrentEducation(initialEducationState);
    }
  }, [editingIndex, educations]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentEducation((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleSaveEducation = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (
      !currentEducation.institutionName ||
      !currentEducation.degree ||
      !currentEducation.startDate
    ) {
      setFormError("Institution, Degree, and Start Date are required.");
      return;
    }
    if (
      currentEducation.endDate &&
      currentEducation.startDate &&
      currentEducation.endDate < currentEducation.startDate
    ) {
      setFormError("End Date cannot be earlier than Start Date.");
      return;
    }
    let success = false;
    if (editingIndex !== null) {
      success = await onUpdateEducation(editingIndex, currentEducation);
    } else {
      success = await onAddEducation(currentEducation);
    }
    if (success) {
      setCurrentEducation(initialEducationState);
      setEditingIndex(null);
      setFormError(null);
    }
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setFormError(null);
  };

  const handleDelete = async (index) => {
    if (window.confirm("Delete this education record?")) {
      const success = await onRemoveEducation(index);
      if (success && editingIndex === index) {
        setEditingIndex(null);
      }
    }
  };

  const handleCancelEdit = () => {
    setCurrentEducation(initialEducationState);
    setEditingIndex(null);
    setFormError(null);
  };

  const handleNext = () => {
    setIsNavigating(true);
    setTimeout(() => {
      if (onNext) {
        onNext();
      }
    }, 1000);
  };

  const inputStyle =
    "block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm";
  const primaryButtonStyle =
    "inline-flex items-center justify-center rounded-md border border-transparent bg-emerald-700 px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 disabled:opacity-50";
  const secondaryButtonStyle =
    "rounded-md border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50";
  const formSaveButtonStyle =
    "inline-flex items-center justify-center rounded-md border border-transparent bg-emerald-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 disabled:opacity-50 min-w-[120px]";
  const formCancelButtonStyle =
    "px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 disabled:opacity-50";

  return (
    <div
      className={`educational-background p-6 md:p-8 ${isMobile ? "pb-24" : ""}`}
    >
      {/* --- UPDATED: Animation with conditional sub-message --- */}
      <SavingAnimation
        isSaving={isLoading}
        message={
          isNavigating
            ? "Proceeding to next step..."
            : "Saving your progress..."
        }
        subMessage={isNavigating ? "Saving..." : undefined}
      />

      <div className="form-header mb-6 text-center">
        <div className="flex justify-center mb-4">
          <img src={EducationIcon} alt="Education" className="w-16 h-16" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">
          Educational Background
        </h2>
        <p className="text-gray-600 mt-1">Share your academic achievements.</p>
      </div>

      {educations && educations.length > 0 && (
        <div className="mb-8 border rounded-lg p-4 bg-gray-50 shadow-sm">
          <h3 className="text-lg font-semibold mb-3 border-b pb-2 text-gray-700">
            Education History ({educations.length})
          </h3>
          <ul className="space-y-3">
            {educations.map((edu, index) => (
              <li
                key={edu.id || `edu-${index}`}
                className="flex flex-col sm:flex-row justify-between sm:items-center p-3 bg-white rounded-md shadow-sm border hover:shadow-md"
              >
                <div className="flex-grow mb-2 sm:mb-0 pr-2">
                  <p className="font-medium text-gray-900">
                    {edu.degree || "N/A"} - {edu.field_of_study || "N/A"}
                  </p>
                  <p className="text-sm text-gray-700 font-semibold">
                    {edu.institution?.name || "Unknown"}
                  </p>
                  <p className="text-sm text-gray-500">
                    {edu.start_date ? edu.start_date.substring(0, 7) : "N/A"} -{" "}
                    {edu.is_current || !edu.end_date
                      ? "Present"
                      : edu.end_date
                      ? edu.end_date.substring(0, 7)
                      : "N/A"}
                  </p>
                </div>
                <div className="flex space-x-2 flex-shrink-0 self-start sm:self-center">
                  <button
                    type="button"
                    onClick={() => handleEdit(index)}
                    disabled={isLoading || editingIndex === index}
                    className="p-1.5 rounded text-blue-600 hover:bg-blue-100 disabled:opacity-50"
                    aria-label="Edit"
                    title="Edit"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(index)}
                    disabled={isLoading}
                    className="p-1.5 rounded text-red-600 hover:bg-red-100 disabled:opacity-50"
                    aria-label="Delete"
                    title="Delete"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <form
        onSubmit={handleSaveEducation}
        className="border rounded-lg p-6 shadow-sm bg-white"
      >
        <h3 className="text-lg font-semibold mb-4 text-gray-700 border-b pb-2">
          {editingIndex !== null
            ? "Edit Education Record"
            : "Add New Education"}
        </h3>
        {formError && (
          <div className="mb-4 rounded-md bg-red-50 p-3 border border-red-200">
            <p className="text-sm text-red-700">{formError}</p>
          </div>
        )}
        <div className="mb-4">
          <label
            htmlFor={`institutionName-${editingIndex ?? "add"}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Institution Name *
          </label>
          <input
            id={`institutionName-${editingIndex ?? "add"}`}
            type="text"
            name="institutionName"
            value={currentEducation.institutionName}
            onChange={handleChange}
            required
            className={inputStyle}
            placeholder="E.g., University of Example"
            disabled={isLoading}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-4">
          <div>
            <label
              htmlFor={`degree-${editingIndex ?? "add"}`}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Degree *
            </label>
            <input
              id={`degree-${editingIndex ?? "add"}`}
              type="text"
              name="degree"
              value={currentEducation.degree}
              onChange={handleChange}
              required
              className={inputStyle}
              placeholder="E.g., Bachelor of Science"
              disabled={isLoading}
            />
          </div>
          <div>
            <label
              htmlFor={`fieldOfStudy-${editingIndex ?? "add"}`}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Field of Study
            </label>
            <input
              id={`fieldOfStudy-${editingIndex ?? "add"}`}
              type="text"
              name="fieldOfStudy"
              value={currentEducation.fieldOfStudy}
              onChange={handleChange}
              className={inputStyle}
              placeholder="E.g., Computer Science"
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Dates Attended *
          </label>
          {/* Mobile: Stack vertically, Desktop: Side by side */}
          <div
            className={`${
              isMobile ? "flex flex-col gap-3" : "flex items-center gap-2"
            }`}
          >
            <input
              aria-label="Start Date"
              type="date"
              name="startDate"
              value={currentEducation.startDate}
              onChange={handleChange}
              required
              className={inputStyle + " flex-1"}
              disabled={isLoading}
            />
            <span className={`text-gray-500 ${isMobile ? "text-sm pl-1" : ""}`}>
              to {!isMobile && ""}
            </span>
            <input
              aria-label="End Date"
              type="date"
              name="endDate"
              value={currentEducation.endDate}
              onChange={handleChange}
              className={inputStyle + " flex-1"}
              min={currentEducation.startDate || undefined}
              disabled={isLoading}
              placeholder="Leave blank if current"
            />
          </div>
        </div>
        <div className="mb-4">
          <label
            htmlFor={`description-${editingIndex ?? "add"}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Description / Notes (Optional)
          </label>
          <textarea
            id={`description-${editingIndex ?? "add"}`}
            name="description"
            value={currentEducation.description}
            onChange={handleChange}
            rows="3"
            className={inputStyle}
            placeholder="E.g., Relevant coursework, honors..."
            disabled={isLoading}
          ></textarea>
        </div>
        <div className="flex justify-end gap-3 mt-6 border-t pt-4">
          {editingIndex !== null && (
            <button
              type="button"
              onClick={handleCancelEdit}
              disabled={isLoading}
              className={formCancelButtonStyle}
            >
              Cancel Edit
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className={`${formSaveButtonStyle} ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting
              ? "Saving..."
              : editingIndex !== null
              ? "Save Changes"
              : "Add Education"}
          </button>
        </div>
      </form>

      {/* Navigation - Added extra bottom padding on mobile to prevent overlap */}
      <div
        className={`flex flex-col sm:flex-row justify-between items-center pt-8 gap-4 border-t mt-10 ${
          isMobile ? "mb-20" : ""
        }`}
      >
        <div className="flex flex-col sm:flex-row gap-x-4 gap-y-4 w-full sm:w-auto order-last sm:order-first">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              disabled={isLoading}
              className={`${secondaryButtonStyle} w-full sm:w-auto`}
            >
              Back
            </button>
          )}
          {onSkipToDashboard && (
            <SkipToDashboardButton
              onSkip={onSkipToDashboard}
              disabled={isLoading}
              className="w-full sm:w-auto"
            />
          )}
        </div>
        <button
          type="button"
          onClick={handleNext}
          disabled={isLoading}
          className={`${primaryButtonStyle} w-full sm:w-auto order-first sm:order-last`}
        >
          Next Step
        </button>
      </div>
    </div>
  );
}

export default EducationalBackground;
